import { MarkupState, SystemType, Line, Point, TradeSymbol, SymbolType } from '@shared/types/tsk';
import { getSymbolAcronym } from './symbolUtils';

export const SYSTEM_COLORS: Record<SystemType, string> = {
  [SystemType.DRAINAGE]: '#8B4513',
  [SystemType.TRADE_WASTE]: '#FF8F00',
  [SystemType.VENT]: '#B8860B',
  [SystemType.AFFLUENT]: '#004D40'
};

/**
 * Renders the provided markup onto a high-resolution canvas over the base image.
 * This ensures the exported plan is consistent and NOT affected by editor zoom/pan.
 */
export const flattenMarkupToCanvas = async (imageSrc: string, markup: MarkupState): Promise<HTMLCanvasElement> => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.src = imageSrc;
  
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error("Could not get 2D context");

  // 1. Draw base image
  ctx.drawImage(img, 0, 0);

  // 2. Draw lines
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  
  markup.lines.forEach((line: Line) => {
    ctx.beginPath();
    ctx.strokeStyle = SYSTEM_COLORS[line.system as SystemType] || '#2563eb';
    ctx.lineWidth = 6;
    line.points.forEach((p: Point, i: number) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();
  });

  // 3. Draw symbols (T-TradeSketch-019 Fidelity)
  markup.symbols.forEach((symbol: TradeSymbol) => {
    const radius = 24;
    const { x, y } = symbol.position;
    
    ctx.save();
    ctx.beginPath();
    ctx.fillStyle = SYSTEM_COLORS[symbol.system as SystemType] || '#2563eb';
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 4;
    
    // CUSTOM ENGINEERING ICONS (Hurdle 19.2)
    if (symbol.type === SymbolType.RISER || symbol.type === SymbolType.DROPPER) {
       // Outer Ring
       ctx.beginPath();
       ctx.arc(x, y, radius, 0, Math.PI * 2);
       ctx.stroke();
       ctx.fill();
       
       // Inner Ring
       ctx.beginPath();
       ctx.arc(x, y, radius * 0.7, 0, Math.PI * 2);
       ctx.strokeStyle = '#FFFFFF';
       ctx.lineWidth = 2;
       ctx.stroke();

       if (symbol.type === SymbolType.RISER) {
          // Dot for Riser
          ctx.beginPath();
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fillStyle = '#FFFFFF';
          ctx.fill();
       } else {
          // X for Dropper
          const size = radius * 0.4;
          ctx.beginPath();
          ctx.moveTo(x - size, y - size);
          ctx.lineTo(x + size, y + size);
          ctx.moveTo(x + size, y - size);
          ctx.lineTo(x - size, y + size);
          ctx.strokeStyle = '#FFFFFF';
          ctx.lineWidth = 3;
          ctx.stroke();
       }
    } else {
       // STANDARD SHAPES
       const isFitting = [
         SymbolType.IO, SymbolType.IOS, SymbolType.CO, SymbolType.COS, 
         SymbolType.ORG, SymbolType.SWIVEL_JOINT, SymbolType.EXPANSION_JOINT
       ].includes(symbol.type);

       if (isFitting) {
         ctx.rect(x - radius, y - radius, radius * 2, radius * 2);
       } else {
         ctx.arc(x, y, radius, 0, Math.PI * 2);
       }
       ctx.fill();
       ctx.stroke();

       // BOLD ACRONYMS (Hurdle 19.1)
       const label = getSymbolAcronym(symbol.type);
       if (label) {
         ctx.font = 'bold 16px Arial';
         ctx.fillStyle = '#FFFFFF';
         ctx.textAlign = 'center';
         ctx.textBaseline = 'middle';
         ctx.fillText(label.toString().toUpperCase(), x, y);
       }
    }
    
    ctx.restore();
  });

  return canvas;
};
